import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import NotFoundPage from "./NotFoundPage";
import { ArrowUpRight, ArrowRight, ChevronRight, CheckCircle2 } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import projects from "../data/projects";
import { getServiceBySlug } from "../data/services";

const setMeta = (attr, key, value) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};
const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};
const injectJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};
const removeJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

export default function ServicePage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (!service) return;
    window.scrollTo(0, 0);
    const canonical = `https://gadgetguymedia.com/services/${service.slug}`;
    document.title = service.title;
    setMeta("name", "description", service.metaDescription);
    setMeta("property", "og:title", service.title);
    setMeta("property", "og:description", service.metaDescription);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", "website");
    setMeta("name", "twitter:title", service.title);
    setMeta("name", "twitter:description", service.metaDescription);
    setLink("canonical", canonical);
    injectJsonLd("jsonld-service-page", {
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.h1,
      serviceType: service.keyword,
      description: service.metaDescription,
      url: canonical,
      provider: {
        "@type": "Organization",
        name: "Gadget Guy Media",
        url: "https://gadgetguymedia.com",
        founder: { "@type": "Person", name: "Robert", url: "https://gadgetguymedia.com/robert" },
      },
      areaServed: "Worldwide",
    });
    injectJsonLd("jsonld-service-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: service.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    injectJsonLd("jsonld-service-breadcrumbs", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://gadgetguymedia.com/" },
        { "@type": "ListItem", position: 2, name: "Services", item: "https://gadgetguymedia.com/#two-ways" },
        { "@type": "ListItem", position: 3, name: service.h1, item: canonical },
      ],
    });
    return () => {
      setLink("canonical", "https://gadgetguymedia.com/");
      removeJsonLd("jsonld-service-page");
      removeJsonLd("jsonld-service-faq");
      removeJsonLd("jsonld-service-breadcrumbs");
    };
  }, [service]);

  if (!service) return <NotFoundPage />;

  const relatedProjects = (service.relatedProjectSlugs || [])
    .map((s) => projects.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <PageShell testId="service-page">
      {/* Hero */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(48px, 8vh, 96px)",
          paddingBottom: "clamp(48px, 8vh, 96px)",
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(1000px 500px at 15% 25%, rgba(11,60,220,0.16), transparent 70%), radial-gradient(700px 400px at 95% 0%, rgba(40,200,240,0.10), transparent 70%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-4" data-testid="service-eyebrow">
            What I Build
          </div>
          <h1
            className="font-condensed font-bold text-white"
            style={{
              fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
            data-testid="service-heading"
          >
            {service.h1}
          </h1>
          <p
            className="mt-5 ggm-gradient-text font-condensed font-bold"
            style={{ fontSize: "clamp(1.15rem, 2.4vw, 1.75rem)", lineHeight: 1.2 }}
            data-testid="service-tagline"
          >
            {service.tagline}
          </p>
          <div className="mt-7 max-w-3xl space-y-5">
            <p className="font-body text-white/80 text-base md:text-lg leading-relaxed">
              {service.intro}
            </p>
            <p className="font-body text-white/80 text-base md:text-lg leading-relaxed">
              {service.intro2}
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <Link to="/#start-a-project" className="ggm-btn-primary" data-testid="service-cta-primary">
              Start a Project <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/pricing"
              className="ggm-btn-secondary"
              data-testid="service-cta-coaching"
            >
              Book a coaching hour <ArrowRight size={16} />
            </Link>
            <Link
              to="/projects"
              className="ggm-btn-secondary"
              data-testid="service-cta-portfolio"
            >
              See my work <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* What I can build */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="service-what-you-get"
      >
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-6">What I Can Build</div>
          <h2
            className="font-condensed font-bold text-white mb-8"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", lineHeight: 1.05 }}
          >
            {service.whatYouGetHeadline}
          </h2>
          <ul className="grid gap-5 md:grid-cols-2">
            {service.whatYouGet.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 pl-5 py-1"
                style={{ borderLeft: "2px solid var(--ggm-signal-cyan)" }}
              >
                <CheckCircle2
                  size={18}
                  className="shrink-0 mt-1"
                  style={{ color: "var(--ggm-signal-cyan)" }}
                  aria-hidden
                />
                <div>
                  <div className="font-condensed font-bold text-white text-base md:text-lg mb-1">
                    {item.title}
                  </div>
                  <p className="font-body text-white/75 text-sm md:text-base leading-relaxed">
                    {item.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How I build it — adapted 5-step visual language */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="service-process"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-6">How I Build It</div>
          <h2
            className="font-condensed font-bold text-white mb-8 uppercase"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", lineHeight: 1.05 }}
          >
            {service.processHeadline}
          </h2>
          <ol className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {service.process.map((p, i) => (
              <li
                key={p.step}
                className="relative"
                data-testid={`service-process-${p.step}`}
              >
                <div
                  className="flex items-center justify-center w-[52px] h-[52px] rounded-full font-condensed font-bold text-white"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--ggm-orbit-blue), var(--ggm-signal-cyan))",
                    border: "1px solid rgba(40,200,240,0.35)",
                    fontSize: 15,
                    boxShadow: "0 8px 24px -8px rgba(40,200,240,0.4)",
                  }}
                >
                  {p.step}
                </div>
                <h3
                  className="mt-5 font-condensed font-bold text-white uppercase tracking-wide"
                  style={{ fontSize: 18 }}
                >
                  {p.title}
                </h3>
                <p className="mt-2 font-body text-white/70 text-sm leading-relaxed">
                  {p.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Relevant work */}
      {relatedProjects.length > 0 && (
        <section
          className="relative"
          style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
          data-testid="service-case-studies"
        >
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="ggm-eyebrow mb-6">Relevant Work</div>
            <h2
              className="font-condensed font-bold text-white mb-8 uppercase"
              style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", lineHeight: 1.05 }}
            >
              Live examples of this in production
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {relatedProjects.map((p) => (
                <Link
                  key={p.slug}
                  to={`/projects/${p.slug}`}
                  className="group flex flex-col rounded-2xl overflow-hidden hover:border-[var(--ggm-signal-cyan)]/40 transition-colors"
                  style={{
                    background: "var(--ggm-surface)",
                    border: "1px solid var(--ggm-border-subtle)",
                  }}
                  data-testid={`service-case-study-${p.slug}`}
                >
                  {p.image && (
                    <div className="aspect-[16/10] overflow-hidden bg-black/40">
                      <img
                        src={p.image}
                        alt={`${p.name} — ${p.tagline}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
                      {p.category}
                    </div>
                    <div className="font-condensed font-bold text-white text-lg mb-1 group-hover:text-[var(--ggm-signal-cyan)] transition-colors">
                      {p.name}
                    </div>
                    <p className="font-body text-white/70 text-sm leading-relaxed mb-3">
                      {p.tagline}
                    </p>
                    <span
                      className="inline-flex items-center gap-1 font-condensed uppercase tracking-widest text-xs"
                      style={{ color: "var(--ggm-signal-cyan)" }}
                    >
                      Read case study <ChevronRight size={13} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="service-faq"
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-6">FAQ</div>
          <h2
            className="font-condensed font-bold text-white mb-8 uppercase"
            style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)", lineHeight: 1.05 }}
          >
            {service.faqHeadline}
          </h2>
          <div className="space-y-3">
            {service.faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-xl p-5"
                style={{
                  background: "var(--ggm-surface)",
                  border: "1px solid var(--ggm-border-subtle)",
                }}
                data-testid={`service-faq-item-${i}`}
              >
                <summary className="cursor-pointer font-condensed font-bold text-white text-base md:text-lg flex items-start justify-between gap-4 list-none">
                  <span>{f.q}</span>
                  <ChevronRight
                    size={20}
                    className="shrink-0 mt-1 text-white/40 group-open:rotate-90 transition-transform"
                  />
                </summary>
                <p className="font-body text-white/75 text-sm md:text-base leading-relaxed mt-3">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Conversion */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(48px, 8vh, 96px)",
          paddingBottom: "clamp(64px, 10vh, 140px)",
          background:
            "radial-gradient(1000px 500px at 30% 20%, rgba(11,60,220,0.14), transparent 70%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2
            className="font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
          >
            Got Something In Your Head?
          </h2>
          <p
            className="mt-4 font-condensed font-bold ggm-gradient-text uppercase"
            style={{ fontSize: "clamp(1.25rem, 3vw, 2rem)", lineHeight: 1.15 }}
          >
            Let's turn it into something real.
          </p>
          <div className="mt-8 flex justify-center">
            <Link to="/#start-a-project" className="ggm-btn-primary" data-testid="service-cta-bottom">
              Start a Project <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
